import React, { useEffect, useState } from "react";

// APP COMPONENTS
// MATERIAL UI COMPONENTS
import Box from "@mui/material/Box";
import Fade from "@mui/material/Fade";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import { Divider, TextField } from "@mui/material";
import ModalHeader from "../../modals/modalHeader";
import { validateString } from "../../../common/validators";

const ModalRoot = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  boxShadow: 24,
  border: "none",
  borderRadius: 12,
  [theme.breakpoints.down("md")]: {
    width: "100% !important",
    height: "95% !important",
    borderRadius: "0 0",
    overflow: "auto",
  },
  [theme.breakpoints.up("md")]: {
    width: "30% !important",
  },
  backgroundColor: "#fff",
  paddingRight: 24,
  paddingLeft: 24,
  paddingBottom: 24,
  paddingTop: 0,
  "&:focus-visible": {
    border: "none",
  },
}));

export default function CreateOrEditWorkShiftModal({
  workShift,
  show,
  closeModal,
  createWorkShift,
  editWorkShift,
}) {
  const [loading, setLoading] = useState(false);

  const [workShiftName, setWorkShiftName] = useState("");
  const [isWorkShiftNameValid, setIsWorkShiftNameValid] = useState(false);

  const [workShiftStartTime, setWorkShiftStartTime] = useState("");
  const [isWorkShiftStartTimeValid, setIsWorkShiftStartTimeValid] =
    useState(false);

  const [workShiftEndTime, setWorkShiftEndTime] = useState("");
  const [isWorkShiftEndTimeValid, setIsWorkShiftEndTimeValid] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (workShift) {
      setIsEditing(true);
      setWorkShiftName(workShift.name || "");
      setWorkShiftStartTime(workShift.start_time || "");
      setWorkShiftEndTime(workShift.end_time || "");
    }
  }, [workShift]);

  const validateFields = () => {
    const startTimeValid = validateString(workShiftStartTime);
    const endTimeValid = validateString(workShiftEndTime);

    setIsWorkShiftStartTimeValid(startTimeValid);
    setIsWorkShiftEndTimeValid(endTimeValid);

    return startTimeValid && endTimeValid;
  };

  const isValidTimeRange = () => {
    // Convert times to comparable values (minutes since midnight)
    const getMinutes = (timeStr) => {
      const [hours, minutes] = timeStr.split(":").map(Number);
      return hours * 60 + minutes;
    };

    const startMinutes = getMinutes(workShiftStartTime);
    const endMinutes = getMinutes(workShiftEndTime);

    return endMinutes > startMinutes;
  };

  const handleSubmit = () => {
    setSubmitted(true);

    if (!validateFields()) {
      return;
    }

    if (!isValidTimeRange()) {
      alert("O horário final deve ser posterior ao horário inicial");
      return;
    }

    setLoading(true);

    const workShiftObj = {
      name: workShiftName,
      start_time: workShiftStartTime,
      end_time: workShiftEndTime,
    };

    if (isEditing) {
      editWorkShift({ id: workShift.id, ...workShiftObj });
    } else {
      createWorkShift(workShiftObj);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", height: "100vh" }}>
        <Stack
          spacing={2}
          sx={{
            m: "auto",
          }}
        >
          <CircularProgress sx={{ "&:focus-visible": { border: "none" } }} />
        </Stack>
      </Box>
    );
  }

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={show}
      onClose={closeModal}
      closeAfterTransition
      sx={{
        zIndex: 5000,
        "&:focus-visible": {
          border: "none",
        },
      }}
    >
      <Fade in={show}>
        <ModalRoot>
          <ModalHeader
            label="Turno de Trabalho"
            margin={0}
            onClose={closeModal}
          />
          <Box
            sx={{
              padding: 2,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <TextField
              name="shiftName"
              required
              fullWidth
              id="shiftName"
              label="Nome"
              value={workShiftName}
              onChange={(e) => setWorkShiftName(e.target.value)}
              error={submitted && !isWorkShiftNameValid}
              helperText={
                submitted && !isWorkShiftNameValid && "Nome é obrigatório"
              }
              autoFocus
            />
            <TextField
              fullWidth
              label="Hora Inicial"
              type="time"
              value={workShiftStartTime}
              onChange={(e) => setWorkShiftStartTime(e.target.value)}
              error={submitted && !isWorkShiftStartTimeValid}
              helperText={
                submitted &&
                !isWorkShiftStartTimeValid &&
                "Hora inicial é obrigatória"
              }
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                step: 300,
              }}
            />

            <TextField
              fullWidth
              label="Hora Final"
              type="time"
              value={workShiftEndTime}
              onChange={(e) => setWorkShiftEndTime(e.target.value)}
              error={submitted && !isWorkShiftEndTimeValid}
              helperText={
                submitted &&
                !isWorkShiftEndTimeValid &&
                "Hora final é obrigatória"
              }
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                step: 300,
              }}
            />
          </Box>

          <Divider sx={{ my: 2 }} />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            sx={{ mt: 4 }}
          >
            {isEditing ? "Atualizar" : "Cadastrar"}
          </Button>
        </ModalRoot>
      </Fade>
    </Modal>
  );
}
